import { Field, Form, Formik, useFormikContext } from "formik";
import Button from "pages/_components/Button";
import GridTable from "pages/_components/GridTable/GridTable";
import Notification from "pages/_components/Notification";
import { bool } from "prop-types";
import React, { useEffect, useState } from "react";
import { Col, Row } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import * as i18n from "util/i18n";
import MainContainer from "pages/_components/MainContainer";
import ResponsiveHeader, { DesktopButtons, MobileButtons } from "pages/_components/header";
import { actions as comexActions } from "reducers/comex";
import { selectors as sessionSelectors } from "reducers/session";
import { store } from "store";
import CustomText from "pages/_components/CustomText/CustomText";
import Image from "pages/_components/Image";
import I18n from "pages/_components/I18n";
import Action from "pages/_components/Action";
import { push } from "connected-react-router";
import ComexStepper from "../_components/ComexStepper";
import DocumentUploader from "../_components/DocumentUploader";
import useComexDocs from "./hooks/userComexDocs";

const FORM_ID = "comex.documentation";
const DOCUMENT_FILE_COUNT = 13;
const StepServiceDocumentation = ({ isDesktop }) => {
    const dispatch = useDispatch();
    const {
        codeStatute,
        codeOfferLetter,
        codeInvoice,
        codeCertificate6401,
        codeAccountingCertification,
        codeServiceCertification,
        codeDDJJ,
        ...comexDocs
    } = useComexDocs();

    const { comexTransfer = {}, comexDocumentation = {}, comexId = null } = useSelector((state) => state.comex);

    const username = sessionSelectors.getUserFullName(store.getState());
    const title = i18n.get("comex.request.title").replace("#{NAME}", username);
    const [isSaved, setIsSaved] = useState(false);
    const [hasRequiredDocs, setHasRequiredDocs] = useState(false);

    const initialValues = {
        documentStatute: comexDocumentation?.documentStatute ?? "",
        documentStatuteName: comexDocumentation?.documentStatuteName ?? "",
        ...Object.fromEntries(
            Array.from({ length: DOCUMENT_FILE_COUNT }, (_, index) => {
                const fileNumber = index + 1;
                return [
                    [`documentFile${fileNumber}`, comexDocumentation?.[`documentFile${fileNumber}`] ?? ""],
                    [`documentFile${fileNumber}Name`, comexDocumentation?.[`documentFile${fileNumber}Name`] ?? ""],
                ];
            }).flat(),
        ),
    };

    const handleSubmit = (values, formikBag) => {
        formikBag.setSubmitting(false);
        dispatch(comexActions.setComexDocument(values));
        saveComexRequest();
        dispatch(push("/comex/service/payment"));
    };

    const [formValues, setFormValues] = useState(initialValues);

    useEffect(() => {
        const allInitialEmpty = Object.values(initialValues).every((value) => value === "");
        const hasChanged = Object.keys(initialValues).some(
            (key) => JSON.stringify(formValues[key]) !== JSON.stringify(initialValues[key]),
        );
        // Habilitar el botón si todos los initialValues son "" o si ha cambiado algo
        setIsSaved(!(allInitialEmpty || hasChanged));
        const requiredDocs = ["offerLetterAndAcceptance", "invoice"]; // documentos obligatorios

        const requiredDocsAreUploaded = requiredDocs.every(
            (docKey) => !!formValues[docKey] && formValues[docKey] !== "",
        );

        setHasRequiredDocs(requiredDocsAreUploaded);
    }, [formValues]);

     const documents = Object.keys(formValues).reduce((acc, key) => {
            const value = formValues[key];
            if (value !== "" && value !== null && value !== undefined) {
                acc[key] = value;
            }
            return acc;
        }, {});

    const saveComexRequest = () => {
        dispatch(comexActions.setComexDocument(formValues));
        const _comexDocumentation = {
            ...comexTransfer,
            comexDocumentation: {
                ...documents,
            },
            step: "stepSERVICEDOCUMENT",
            comexId,
        };

        dispatch(comexActions.createComexTransfer(_comexDocumentation));
        setIsSaved(true);
    };

    return (
        <>
            {isDesktop && <ComexStepper isDesktop={isDesktop} currentStep={6} />}
            <Notification scopeToShow="ComexNotification" />
            <ResponsiveHeader ticket metaTitle="transfers.comex.title" backLinkTo="/comex/retention">
                <DesktopButtons>
                    <Button className="btn btn-outline btn-block small" onClick={saveComexRequest} disabled={isSaved}>
                        <Image
                            src={isSaved ? "images/figma/icon/check.svg" : "images/figma/icon/save.svg"}
                            className="icon-medium svg-icon"
                        />
                        <I18n id={isSaved ? "comex.transfer.save.ok.btn" : "comex.transfer.save.btn"} />
                    </Button>
                </DesktopButtons>
                <MobileButtons>
                    <Action labelKey="comex.transfer.save.btn" handleClick={saveComexRequest} disabled={isSaved} />
                </MobileButtons>
            </ResponsiveHeader>

            <MainContainer showLoader={false}>
                <Notification scopeToShow="UploadNotification" />
                <h2>{title}</h2>
                <I18n component="h3" id="comex.documentation.title" />
                <Formik initialValues={initialValues} onSubmit={handleSubmit}>
                    {({ isSubmitting, values }) => {
                        setFormValues(values);
                        return (
                            <Form>
                                <GridTable className="form">
                                    <GridTable.Container columns={12} columnsTemplate="fixed">
                                        <GridTable.Data
                                            columnStart={2}
                                            flexDirection="column"
                                            columnWidth={4}
                                            rowStart={1}>
                                            <CustomText
                                                text="comex.documentation.mandatory.label"
                                                textType="I18N"
                                                className="comex-subtitle mg-left"
                                            />
                                        </GridTable.Data>
                                        <GridTable.Data columnStart={2} flexDirection="column" columnWidth={10}>
                                            <CustomText
                                                className="document-title"
                                                text={`${FORM_ID}.offerLetterAndAcceptance.label`}
                                                textType="I18N"
                                            />
                                            <Field
                                                component={DocumentUploader}
                                                idForm={FORM_ID}
                                                name="offerLetterAndAcceptance"
                                                label="file.upload.input.label"
                                                extensionDocument="pdf"
                                                documentCode={codeOfferLetter}
                                                defaultDocument={{
                                                    documentId: comexDocumentation?.offerLetterAndAcceptance,
                                                    documentName: comexDocumentation?.offerLetterAndAcceptanceName,
                                                }}
                                                withDocumentName
                                                overrideDocumentName="CartaOferta"
                                            />
                                        </GridTable.Data>
                                        <GridTable.Data columnStart={2} flexDirection="column" columnWidth={10}>
                                            <CustomText
                                                className="document-title"
                                                text={`${FORM_ID}.invoice.label`}
                                                textType="I18N"
                                            />
                                            <Field
                                                component={DocumentUploader}
                                                idForm={FORM_ID}
                                                name="invoice"
                                                label="file.upload.input.label"
                                                extensionDocument="pdf"
                                                documentCode={codeInvoice}
                                                defaultDocument={{
                                                    documentId: comexDocumentation?.invoice,
                                                    documentName: comexDocumentation?.invoiceName,
                                                }}
                                                withDocumentName
                                                overrideDocumentName="Factura"
                                            />
                                        </GridTable.Data>
                                        <GridTable.Data columnStart={2} flexDirection="column" columnWidth={10}>
                                            <CustomText
                                                text="comex.documentation.related.label"
                                                textType="I18N"
                                                className="comex-subtitle mg-left"
                                            />
                                        </GridTable.Data>
                                        <GridTable.Data columnStart={2} flexDirection="column" columnWidth={10}>
                                            <CustomText
                                                className="document-title"
                                                text={`${FORM_ID}.certificate6401.label`}
                                                textType="I18N"
                                            />
                                            <Field
                                                component={DocumentUploader}
                                                idForm={FORM_ID}
                                                name="certificate6401"
                                                label="file.upload.input.label"
                                                extensionDocument="pdf"
                                                documentCode={codeCertificate6401}
                                                defaultDocument={{
                                                    documentId: comexDocumentation?.certificate6401,
                                                    documentName: comexDocumentation?.certificate6401Name,
                                                }}
                                                withDocumentName
                                                overrideDocumentName="CertificadoValidacion"
                                            />
                                        </GridTable.Data>
                                        <GridTable.Data columnStart={2} flexDirection="column" columnWidth={10}>
                                            <CustomText
                                                className="document-title"
                                                text={`${FORM_ID}.accountingCertification.label`}
                                                textType="I18N"
                                            />
                                            <Field
                                                component={DocumentUploader}
                                                idForm={FORM_ID}
                                                name="accountingCertification"
                                                label="file.upload.input.label"
                                                extensionDocument="pdf"
                                                documentCode={codeAccountingCertification}
                                                defaultDocument={{
                                                    documentId: comexDocumentation?.accountingCertification,
                                                    documentName: comexDocumentation?.accountingCertificationName,
                                                }}
                                                withDocumentName
                                                overrideDocumentName="CertificacionContableDeDeuda"
                                            />
                                        </GridTable.Data>
                                        <GridTable.Data columnStart={2} flexDirection="column" columnWidth={10}>
                                            <CustomText
                                                className="document-title"
                                                text={`${FORM_ID}.ddjj.label`}
                                                textType="I18N"
                                            />
                                            <Field
                                                component={DocumentUploader}
                                                idForm={FORM_ID}
                                                name="ddjj"
                                                label="file.upload.input.label"
                                                extensionDocument="pdf"
                                                documentCode={codeDDJJ}
                                                defaultDocument={{
                                                    documentId: comexDocumentation?.ddjj,
                                                    documentName: comexDocumentation?.ddjjName,
                                                }}
                                                withDocumentName
                                                overrideDocumentName="DDJJAclaratoria"
                                            />
                                        </GridTable.Data>
                                        <GridTable.Data columnStart={2} flexDirection="column" columnWidth={10}>
                                            <CustomText
                                                className="document-title"
                                                text={`${FORM_ID}.serviceCertification.label`}
                                                textType="I18N"
                                            />
                                            <Field
                                                component={DocumentUploader}
                                                idForm={FORM_ID}
                                                name="serviceCertification"
                                                label="file.upload.input.label"
                                                extensionDocument="pdf"
                                                documentCode={codeServiceCertification}
                                                defaultDocument={{
                                                    documentId: comexDocumentation?.serviceCertification,
                                                    documentName: comexDocumentation?.serviceCertificationName,
                                                }}
                                                withDocumentName
                                                overrideDocumentName="CertificacionPrestacionServicio"
                                            />
                                        </GridTable.Data>
                                        <GridTable.Data columnStart={2} flexDirection="column" columnWidth={4}>
                                            <CustomText
                                                text="comex.documentation.subtitle"
                                                textType="I18N"
                                                className="comex-subtitle mg-left"
                                            />
                                        </GridTable.Data>
                                        <GridTable.Data columnStart={2} flexDirection="column" columnWidth={10}>
                                            <CustomText
                                                className="document-title"
                                                text={`${FORM_ID}.statute.label`}
                                                textType="I18N"
                                            />
                                            <Field
                                                component={DocumentUploader}
                                                idForm={FORM_ID}
                                                name="documentStatute"
                                                label="file.upload.input.label"
                                                extensionDocument="pdf"
                                                documentCode={codeStatute}
                                                defaultDocument={{
                                                    documentId: comexDocumentation?.documentStatute,
                                                    documentName: comexDocumentation?.documentStatuteName,
                                                }}
                                                withDocumentName
                                            />
                                        </GridTable.Data>
                                        {Array.from({ length: DOCUMENT_FILE_COUNT }, (_, index) => {
                                            const fileNumber = index + 1;
                                            const documentKey = `documentFile${fileNumber}`;
                                            return (
                                                <GridTable.Data
                                                    key={fileNumber}
                                                    columnStart={2}
                                                    flexDirection="column"
                                                    columnWidth={10}>
                                                    <CustomText
                                                        className="document-title"
                                                        text={`${FORM_ID}.fileUpload${fileNumber}`}
                                                        textType="I18N"
                                                    />
                                                    <Field
                                                        component={DocumentUploader}
                                                        idForm={FORM_ID}
                                                        name={documentKey}
                                                        label="file.upload.input.label"
                                                        extensionDocument="pdf"
                                                        documentCode={comexDocs[`codeFile${fileNumber}`] + "_" + fileNumber}
                                                        defaultDocument={{
                                                            documentId: comexDocumentation?.[documentKey],
                                                            documentName: comexDocumentation?.[`${documentKey}Name`],
                                                        }}
                                                        withDocumentName
                                                    />
                                                </GridTable.Data>
                                            );
                                        })}
                                    </GridTable.Container>
                                </GridTable>
                                <Row className="form-actions">
                                    <Col xs={12} md={4} mdOffset={4}>
                                        <Button
                                            type="submit"
                                            className="btn-primary"
                                            label="global.continue"
                                            loading={isSubmitting}
                                            disabled={!hasRequiredDocs}
                                        />
                                    </Col>
                                </Row>
                            </Form>
                        );
                    }}
                </Formik>
            </MainContainer>
        </>
    );
};

StepServiceDocumentation.propTypes = {
    isDesktop: bool.isRequired,
};

export default StepServiceDocumentation;
